/**
 * 日別ビューの旅カード
 *
 * - その日が旅の期間なら「○○ 2日目」とその日のプランの達成状況を出す
 * - 過去の同じ日に旅をしていたら「n年前の今日」として思い出を出す
 */

import { useQuery } from '@tanstack/react-query'
import { CircleCheck, CircleDashed, History, Luggage, MapPin } from 'lucide-react'
import { useNavigate } from 'react-router'

import { getTripsOnDate } from '@/api/services/trips'
import type { Trip, TripMemory, TripOnDate } from '@/api/types/trip'
import { Badge } from '@/components/ui/badge'
import { shortPrefectureName } from '@/lib/prefectures'
import { cn } from '@/lib/utils'
import { durationLabel } from '@/pages/trips/components/trip-utils'
import { PATHS } from '@/routes/paths'
import { useAppStore } from '@/store/useAppStore'

import { ImmichThumbnail } from './activity-card/immich-thumbnail'

/** プランのスポットはこれを超えたら折りたたむ */
const MAX_PLAN_SPOTS = 6

type Props = {
  /** YYYY-MM-DD */
  date: string
  className?: string
}

const TripCover = ({ trip, className }: { trip: Trip; className?: string }) => (
  <div className={cn('shrink-0 overflow-hidden rounded-lg bg-muted', className)}>
    {trip.cover_asset_id ? (
      <ImmichThumbnail assetId={trip.cover_asset_id} alt={trip.title} size="thumbnail" className="h-full w-full" />
    ) : (
      <div className="flex h-full w-full items-center justify-center text-muted-foreground">
        <MapPin className="size-5" />
      </div>
    )}
  </div>
)

const PlanSpotList = ({ trip }: { trip: TripOnDate }) => {
  const spots = trip.plan_spots
  const visitedCount = spots.filter((spot) => spot.visited).length
  const shown = spots.slice(0, MAX_PLAN_SPOTS)

  return (
    <div className="border-t px-3 py-2">
      <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
        <span>この日のプラン</span>
        <span>
          {visitedCount} / {spots.length} ヶ所
        </span>
      </div>
      <ul className="space-y-0.5">
        {shown.map((spot) => (
          <li key={`${spot.plan_id}-${spot.place_id}`} className="flex items-center gap-1.5 text-sm">
            {spot.visited ? (
              <CircleCheck className="size-3.5 shrink-0 text-emerald-500" />
            ) : (
              <CircleDashed className="size-3.5 shrink-0 text-muted-foreground" />
            )}
            <span className={cn('truncate', !spot.visited && 'text-muted-foreground')}>{spot.name}</span>
          </li>
        ))}
      </ul>
      {spots.length > shown.length && (
        <p className="mt-0.5 text-xs text-muted-foreground">ほか {spots.length - shown.length} ヶ所</p>
      )}
    </div>
  )
}

const CurrentTripCard = ({ trip, onOpen }: { trip: TripOnDate; onOpen: () => void }) => (
  <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
    <button type="button" onClick={onOpen} className="flex w-full items-center gap-3 p-2 text-left">
      <TripCover trip={trip} className="size-14" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Luggage className="size-3.5" />
          <span>
            {trip.day_number}日目 / {durationLabel(trip)}
          </span>
          {trip.status === 'draft' && (
            <Badge variant="secondary" className="h-4 bg-amber-500/80 px-1 text-[10px] text-white">
              未確認
            </Badge>
          )}
        </div>
        <p className="truncate font-semibold">{trip.title}</p>
        <p className="truncate text-xs text-muted-foreground">
          この日 {trip.day_visit_count} ヶ所を訪問
          {trip.prefectures.length > 0 && ` ・ ${trip.prefectures.map(shortPrefectureName).join('・')}`}
        </p>
      </div>
    </button>
    {trip.plan_spots.length > 0 && <PlanSpotList trip={trip} />}
  </div>
)

const MemoryCard = ({ trip, onOpen }: { trip: TripMemory; onOpen: () => void }) => (
  <button
    type="button"
    onClick={onOpen}
    className="flex w-full items-start gap-3 rounded-xl border bg-linear-to-br from-amber-500/10 to-transparent p-2 text-left shadow-sm"
  >
    <TripCover trip={trip} className="size-16" />
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400">
        <History className="size-3.5" />
        <span>{trip.years_ago}年前の今日</span>
        <span className="text-muted-foreground">
          {trip.day_number}日目 / {durationLabel(trip)}
        </span>
      </div>
      <p className="truncate font-semibold">{trip.title}</p>
      {trip.story_summary ? (
        <p className="line-clamp-2 text-xs text-muted-foreground">{trip.story_summary}</p>
      ) : (
        <p className="truncate text-xs text-muted-foreground">
          {trip.place_count}ヶ所 ・ {trip.photo_count}枚
        </p>
      )}
    </div>
  </button>
)

export const TripDayCards = ({ date, className }: Props) => {
  const navigate = useNavigate()
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)

  const { data } = useQuery({
    queryKey: ['trips', 'on-date', date],
    queryFn: () => getTripsOnDate(date),
    enabled: hasFullAccess,
  })

  if (!data || (data.trips.length === 0 && data.memories.length === 0)) return null

  const openTrip = (tripId: number) => navigate(PATHS.TRIPS.DETAIL.replace(':trip_id', String(tripId)))

  return (
    <div className={cn('space-y-2', className)}>
      {data.trips.map((trip) => (
        <CurrentTripCard key={trip.id} trip={trip} onOpen={() => openTrip(trip.id)} />
      ))}
      {data.memories.map((trip) => (
        <MemoryCard key={trip.id} trip={trip} onOpen={() => openTrip(trip.id)} />
      ))}
    </div>
  )
}
